import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(timezone);

/**
 * Interface for time labels
 */
interface TimeLabels {
  amLabel: string;
  pmLabel: string;
}

/**
 * Interface for dropdown time item
 */
interface TimeItem {
  twelveHours: string;
  twentyFourHours: string;
  period: string;
  disabled: boolean;
}

/**
 * Creates a mask for 24-hour time format input
 * https://github.com/text-mask/text-mask/issues/550#issuecomment-307551239
 */
export function timeMask24(value: string): Array<RegExp | string> {
  const chars = value.split("");
  let hours: Array<RegExp | string>;
  if (chars.length === 1) {
    hours = [/[0-9]/, /[0-9]/];
  } else if (chars[1] === ":") {
    // To add the initial zero
    if (chars[0] == "0") {
      hours = ["0", "0"];
    } else {
      hours = ["0", /[0-9]/];
    }
  } else {
    hours = [/[0-2]/, chars[0] == "2" ? /[0-3]/ : /[0-9]/];
  }
  const minutes: Array<RegExp | string> = [/[0-5]/, /[0-9]/];
  return hours.concat(":").concat(minutes);
}

/**
 * Creates a mask for 12-hour time format input
 */
export function timeMask12(value: string): Array<RegExp | string> {
  const chars = value.split("");
  const hours: Array<RegExp | string> = [
    /[0-1]/,
    chars[0] == "1" ? /[0-2]/ : /[0-9]/,
  ];
  const minutes: Array<RegExp | string> = [/[0-5]/, /[0-9]/];
  return hours.concat(":").concat(minutes);
}

/**
 * Creates a mask for 12-hour time format input with AM/PM period
 */
export function timeMaskWithPeriod(value: string): Array<RegExp | string> {
  const chars = value.split("");
  let hours: Array<RegExp | string>;
  if (chars.length === 1) {
    hours = [/[0-9]/, /[0-9]/];
  } else if (chars[1] === ":") {
    // To add the initial zero
    hours = ["0", /[0-9]/];
  } else if (chars[0] == "1") {
    hours = [/[0-1]/, /[0-2]/];
  } else {
    hours = [/[0-1]/, chars[0] == "0" ? /[1-9]/ : /[0-9]/];
  }
  const minutes: Array<RegExp | string> = [/[0-5]/, /[0-9]/];
  const meridiem: Array<RegExp | string> = [" ", /(A|\P)/i, "M"];
  return hours.concat(":").concat(minutes).concat(meridiem);
}

/**
 * Gets dropdown items for time selection
 */
export function getDropdownItems(
  { amLabel, pmLabel }: TimeLabels,
  firstAcceptableTime?: dayjs.Dayjs,
  timezone?: string
): TimeItem[] {
  const hours = [12, ...Array.from({ length: 11 }, (_, i) => 1 + i * 1)];
  const formatHours = (hours: number): string => {
    return hours < 10 ? `0${hours}` : `${hours}`;
  };

  const getHours = (isAm: boolean): TimeItem[] => {
    const res: TimeItem[] = [];

    hours.forEach((n) => {
      let hour = n;
      if (isAm) {
        if (hour === 12) {
          hour = 0;
        }
      } else {
        if (hour !== 12) {
          hour += 12;
        }
      }

      const twelveHours = `${formatHours(n)}:00`;
      const twentyFourHours = `${formatHours(hour)}:00`;
      const twelveHoursHalf = `${formatHours(n)}:30`;
      const twentyFourHoursHalf = `${formatHours(hour)}:30`;

      res.push({
        twelveHours,
        twentyFourHours,
        period: isAm ? amLabel : pmLabel,
        disabled: timeIsAfterHHmmFormattedTime(
          firstAcceptableTime,
          twentyFourHours,
          timezone
        ),
      });
      res.push({
        twelveHours: twelveHoursHalf,
        twentyFourHours: twentyFourHoursHalf,
        period: isAm ? amLabel : pmLabel,
        disabled: timeIsAfterHHmmFormattedTime(
          firstAcceptableTime,
          twentyFourHoursHalf,
          timezone
        ),
      });
    });

    return res;
  };

  const AM = getHours(true);
  const PM = getHours(false);

  return [...AM, ...PM];
}

/**
 * Checks if a time is after a given formatted time
 */
export function timeIsAfterHHmmFormattedTime(
  firstAcceptableTime?: dayjs.Dayjs | string | Date,
  HHmmFormattedTime?: string,
  timezone?: string
): boolean {
  if (!firstAcceptableTime) {
    return false;
  }

  if (dayjs.isDayjs(firstAcceptableTime)) {
    const compareToTimeSlot = firstAcceptableTime
      .tz(timezone)
      .startOf("day")
      .add(dayjs(HHmmFormattedTime, "HH:mm").hour(), "hour")
      .add(dayjs(HHmmFormattedTime, "HH:mm").minute(), "minute");
    return firstAcceptableTime.isAfter(compareToTimeSlot);
  }

  return dayjs(dayjs(firstAcceptableTime).format("HH:mm"), "HH:mm").isAfter(
    dayjs(HHmmFormattedTime, "HH:mm")
  );
}

/**
 * Floors time to nearest half hour
 */
export function floorTime(time: string): string {
  // eslint-disable-next-line prefer-const
  let [hours, minutes] = time.split(":");
  let minutesNum = Number(minutes);

  if (minutesNum >= 30) {
    minutesNum = 30;
  } else {
    minutesNum = 0;
  }

  minutes = minutesNum > 9 ? `${minutesNum}` : `0${minutesNum}`;
  return `${hours}:${minutes}`;
}

/**
 * Transforms time in 12 hours format to 24 hours
 */
export function TimeIn12To24(
  time: string,
  period: string,
  { amLabel, pmLabel }: TimeLabels
): string | null {
  // 00:00 -> 5 chars
  if (time.length < 5) {
    return null;
  }
  // eslint-disable-next-line prefer-const
  let [hours, minutes] = time.split(":");
  if (hours === "12") {
    hours = period === amLabel ? "00" : hours;
  } else if (period === pmLabel) {
    hours = `${Number(hours) + 12}`;
  }

  return `${hours}:${minutes}`;
}

/**
 * Validates if input time is in correct format
 */
export function isInputValid(
  time: string | null | undefined,
  isTwentyFourHoursFormat: boolean,
  { amLabel, pmLabel }: TimeLabels
): boolean {
  if (!time) {
    return false;
  }
  time = time.toUpperCase();
  let regex = new RegExp("^(2[0-3]|[01][0-9]):([0-5][0-9])$");
  if (!isTwentyFourHoursFormat) {
    regex = new RegExp(
      `^(1[0-2]|0[1-9]):[0-5][0-9] (${amLabel}|${pmLabel})$`,
      "i"
    );
  }
  return regex.test(time);
}

/**
 * Transforms time into the wanted format (12 or 24 hour)
 */
export function TransformIntoWantedFormat(
  time: string,
  toTwentyFour: boolean,
  { amLabel, pmLabel }: TimeLabels
): [string, string] {
  const LABELS = { amLabel, pmLabel };
  let initialPeriod: string;
  let initialValue = time;

  if (isTwelveFormat(initialValue, LABELS)) {
    initialPeriod = initialValue.substr(6, 2);
    initialValue = initialValue.substr(0, 5);
    if (toTwentyFour) {
      const convertedValue = TimeIn12To24(initialValue, initialPeriod, LABELS);
      if (convertedValue !== null) {
        initialValue = convertedValue;
      }
    }
  } else if (isTweentyFourFormat(initialValue)) {
    initialValue = initialValue.substr(0, 5);
    initialPeriod = Number(initialValue.substr(0, 2)) < 12 ? amLabel : pmLabel;
    if (!toTwentyFour) {
      let hours: number | string = Number(initialValue.substr(0, 2));
      const minutes = initialValue.substr(3, 2);
      if (hours === 0) {
        hours = 12;
      } else if (hours > 12) {
        hours -= 12;
      }
      hours = hours < 10 ? `0${hours}` : `${hours}`;
      initialValue = `${hours}:${minutes}`;
    }
  } else {
    initialPeriod = pmLabel;
  }

  return [initialValue, initialPeriod];
}

/**
 * Checks if time is in 24-hour format
 */
export function isTweentyFourFormat(time: string): boolean {
  const regex = new RegExp("^(2[0-3]|[01][0-9]):([0-5][0-9])$");
  return regex.test(time);
}

/**
 * Checks if time is in 12-hour format
 */
export function isTwelveFormat(
  time: string | null | undefined,
  { amLabel, pmLabel }: TimeLabels
): boolean {
  if (!time || !amLabel || !pmLabel) {
    return false;
  }
  time = time.toUpperCase();
  const regex = new RegExp(
    `^(1[0-2]|0[1-9]):[0-5][0-9] (${amLabel}|${pmLabel})$`,
    "i"
  );
  return regex.test(time);
}

/**
 * Gets default time (current time + 30 min, rounded to nearest half hour)
 */
export function getDefaultTime({ amLabel, pmLabel }: TimeLabels): string {
  const helper = 1000 * 60 * 30;
  const now = new Date();
  now.setMinutes(now.getMinutes() + 30);
  const rounded = new Date(Math.round(now.getTime() / helper) * helper);
  let hours: number | string = rounded.getHours();
  let minutes: number | string = rounded.getMinutes();
  let period = amLabel;

  // istanbul ignore next
  if (hours > 12) {
    hours -= 12;
    period = pmLabel;
  }
  if (hours === 0) {
    hours = 12;
  }

  minutes = minutes <= 9 ? `0${minutes}` : minutes;
  hours = hours <= 9 ? `0${hours}` : hours;

  return `${hours}:${minutes} ${period}`;
}

/**
 * Completes incomplete time input
 */
export function completeUncompletedTime(time: string): string {
  const len = time.length;
  const chars = time.split("");
  switch (len) {
    case 1:
      return `${chars[0]}0:00`;
    case 2:
    case 3:
      return `${chars[0]}${chars[1]}:00`;
    case 4:
      return `${chars[0]}${chars[1]}:${chars[3]}0`;
    case 5:
      return `${chars[0]}${chars[1]}:${chars[3]}${chars[4]}`;
    default:
      return "";
  }
}
